import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/data/content";

const links = [
  { id: "home", label: "Home" },
  { id: "about", label: "About Us" },
  { id: "role", label: "Our Role" },
  { id: "scope", label: "Our Scope" },
  { id: "team", label: "Team" },
  { id: "contact", label: "Contact" },
];

export const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-ink/80 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo-link"
          onClick={() => scrollTo("home")}
          className="bg-white rounded-full px-3 py-1.5 hover:scale-[1.05] transition-transform shadow-[0_0_22px_rgba(0,245,212,0.35)]"
          aria-label="iCONTENT home"
        >
          <img src={LOGO_URL} alt="iCONTENT" className="h-6 sm:h-7 w-auto" />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="text-[13px] tracking-[0.18em] uppercase text-slate-300 hover:text-glow transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <button
          data-testid="nav-mobile-menu-button"
          className="lg:hidden text-slate-200 p-2"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-ink/95 backdrop-blur-xl border-b border-white/5 px-6 pb-6 pt-2 flex flex-col gap-4"
          >
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => {
                  setOpen(false);
                  scrollTo(l.id);
                }}
                className="text-left font-syne text-lg text-slate-200 hover:text-glow transition-colors"
              >
                {l.label}
              </button>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
